import { useState, Fragment, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import ReactECharts from 'echarts-for-react';
import toast from 'react-hot-toast';

// Lemon.js will be loaded dynamically
import {
  UserGroupIcon,
  CircleStackIcon,
  SparklesIcon,
  ChartBarIcon,
  CheckIcon,
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CreditCardIcon,
  ClockIcon,
  BuildingOffice2Icon,
  EnvelopeIcon,
  PlusIcon,
  CubeIcon,
  ShoppingCartIcon,
  DocumentArrowDownIcon,
  ReceiptPercentIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import BetaTrialStatus from '../../components/BetaTrialStatus';

// Transaction History Component
function TransactionHistory({ companyId, formatCurrencyAmount }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  const loadTransactions = useCallback(async (cursor = null) => {
    console.log('[TransactionHistory] loadTransactions called', { cursor, companyId });
    try {
      setLoading(true);
      const params = {};
      if (cursor) params.after = cursor;
      if (companyId) params.companyId = companyId;
      
      console.log('[TransactionHistory] Fetching with params:', params);
      const response = await apiHelpers.getTransactionHistory(params);
      const data = response.data;
      console.log('[TransactionHistory] Response:', { 
        transactionCount: data.transactions?.length, 
        hasMore: data.hasMore, 
        nextCursor: data.meta?.pagination?.next 
      });
      
      if (cursor) {
        setTransactions(prev => [...prev, ...data.transactions]);
      } else {
        setTransactions(data.transactions || []);
      }
      setHasMore(data.hasMore || false);
      setNextCursor(data.meta?.pagination?.next || null);
    } catch (error) {
      console.error('[TransactionHistory] Failed to load transactions:', error);
      toast.error('Failed to load transaction history');
      // Reset state on error
      if (!cursor) {
        setTransactions([]);
        setHasMore(false);
        setNextCursor(null);
      }
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    console.log('[TransactionHistory] useEffect triggered, calling loadTransactions');
    loadTransactions();
  }, [loadTransactions]);

  const handleDownloadInvoice = async (transaction) => {
    try {
      setDownloadingInvoice(transaction.id);
      // Pass companyId for Super/Managing Admin accounts
      const params = companyId ? { companyId } : {};
      const response = await apiHelpers.getTransactionInvoice(transaction.id, params);
      
      // Open the invoice URL in a new tab (it's a temporary URL)
      window.open(response.data.url, '_blank');
      toast.success('Invoice downloaded');
    } catch (error) {
      console.error('Failed to download invoice:', error);
      toast.error(error.response?.data?.error || 'Failed to download invoice');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Completed' },
      billed: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'Billed' },
      ready: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'Ready' },
      past_due: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Past Due' },
      canceled: { color: 'bg-surface-100 text-surface-600 dark:bg-surface-800 dark:text-surface-400', label: 'Canceled' },
      draft: { color: 'bg-surface-100 text-surface-600 dark:bg-surface-800 dark:text-surface-400', label: 'Draft' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="card mt-6">
        <div className="p-4 border-b border-surface-200 dark:border-surface-800">
          <h2 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
            <ReceiptPercentIcon className="w-5 h-5 text-primary-500" />
            Transaction History
          </h2>
        </div>
        <div className="p-8 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="card mt-6">
      <div className="p-4 border-b border-surface-200 dark:border-surface-800 flex items-center justify-between">
        <h2 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
          <ReceiptPercentIcon className="w-5 h-5 text-primary-500" />
          Transaction History
        </h2>
        <button
          onClick={() => loadTransactions()}
          className="btn-ghost text-sm"
          disabled={loading}
        >
          <ArrowPathIcon className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      
      {transactions.length === 0 ? (
        <div className="p-8 text-center text-surface-500">
          <ReceiptPercentIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No transactions yet</p>
          <p className="text-sm mt-1">Your payment history will appear here</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-50 dark:bg-surface-800/50">
                  <th className="text-left text-xs font-medium text-surface-500 uppercase tracking-wider p-4">Date</th>
                  <th className="text-left text-xs font-medium text-surface-500 uppercase tracking-wider p-4">Description</th>
                  <th className="text-left text-xs font-medium text-surface-500 uppercase tracking-wider p-4">Status</th>
                  <th className="text-right text-xs font-medium text-surface-500 uppercase tracking-wider p-4">Amount</th>
                  <th className="text-right text-xs font-medium text-surface-500 uppercase tracking-wider p-4">Invoice</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200 dark:divide-surface-700">
                {transactions.map((txn) => (
                  <tr key={txn.id} className="hover:bg-surface-50 dark:hover:bg-surface-800/30">
                    <td className="p-4 text-sm text-surface-600 dark:text-surface-400">
                      {txn.billedAt ? new Date(txn.billedAt).toLocaleDateString() : 
                       txn.createdAt ? new Date(txn.createdAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-4">
                      <div className="text-sm text-surface-900 dark:text-white">
                        {txn.details?.lineItems?.[0]?.productName || 'Payment'}
                      </div>
                      {txn.invoiceNumber && (
                        <div className="text-xs text-surface-500">
                          Invoice #{txn.invoiceNumber}
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      {getStatusBadge(txn.status)}
                    </td>
                    <td className="p-4 text-right">
                      <span className="text-sm font-medium text-surface-900 dark:text-white">
                        {txn.total ? formatCurrencyAmount(parseInt(txn.total) / 100, txn.currencyCode) : '-'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      {(txn.status === 'completed' || txn.status === 'billed') && (
                        <button
                          onClick={() => handleDownloadInvoice(txn)}
                          disabled={downloadingInvoice === txn.id}
                          className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm font-medium disabled:opacity-50"
                        >
                          {downloadingInvoice === txn.id ? (
                            <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <DocumentArrowDownIcon className="w-4 h-4" />
                          )}
                          Download
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {hasMore && nextCursor && (
            <div className="p-4 border-t border-surface-200 dark:border-surface-800 text-center">
              <button
                onClick={() => loadTransactions(nextCursor)}
                disabled={loading}
                className="btn-secondary"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-surface-500 border-t-transparent rounded-full animate-spin mr-2" />
                    Loading...
                  </>
                ) : (
                  'Load More'
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Plan feature comparison
const PLAN_FEATURES = {
  starter: {
    color: 'surface',
    features: ['1 User Seat', '500 MB Storage', '1 Dashboard', 'Weekly Sync', '100 One-time Credits']
  },
  basic: {
    color: 'blue',
    features: ['1 User Seat', '10 GB Storage', '3 Dashboards', 'Daily Sync', '400 Monthly Credits']
  },
  pro: {
    color: 'indigo',
    features: ['5 User Seats', '100 GB Storage', 'Unlimited Dashboards', 'Hourly Sync', '2000 Monthly Credits']
  },
  business: {
    color: 'purple',
    features: ['10 User Seats', '500 GB Storage', 'Unlimited Dashboards', 'Hourly Sync', '6000 Monthly Credits']
  },
  enterprise: {
    color: 'amber',
    features: ['Custom Seats', 'Custom Storage', 'Unlimited Dashboards', 'Hourly Sync', 'Custom Credits']
  }
};

export default function BillingUsage() {
  const queryClient = useQueryClient();
  const { getCurrentCompany, getCurrentCompanyId, isSuperUser } = useAuthStore();
  const company = getCurrentCompany();
  const companyId = getCurrentCompanyId();
  
  // Check if Super/Managing Admin has selected a company
  const noCompanySelected = isSuperUser() && !companyId;
  
  // URL params for opening modals from other pages
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showSeatsModal, setShowSeatsModal] = useState(false);
  const [selectedCreditPack, setSelectedCreditPack] = useState(null);
  const [additionalSeats, setAdditionalSeats] = useState(1);
  // Display currency for showing prices (also used for checkout)
  const [displayCurrency, setDisplayCurrency] = useState('');
  const [enterpriseForm, setEnterpriseForm] = useState({
    companyName: company?.name || '',
    contactName: '',
    email: '',
    phone: '',
    expectedSeats: '',
    expectedStorage: '',
    message: ''
  });
  
  // Lemon Squeezy checkout state
  const [lemonSqueezyLoaded, setLemonSqueezyLoaded] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Load Lemon.js
  useEffect(() => {
    const loadLemonSqueezy = async () => {
      // Check if already loaded and initialized
      if (window.LemonSqueezy) {
        setLemonSqueezyLoaded(true);
        console.log('[LemonSqueezy] Already initialized');
        return;
      }

      // Helper to wait for createLemonSqueezy function to be available
      const waitForCreateFunction = (maxAttempts = 50, interval = 100) => {
        return new Promise((resolve, reject) => {
          let attempts = 0;
          const checkInterval = setInterval(() => {
            attempts++;
            if (window.createLemonSqueezy) {
              clearInterval(checkInterval);
              resolve(window.createLemonSqueezy);
            } else if (attempts >= maxAttempts) {
              clearInterval(checkInterval);
              reject(new Error('createLemonSqueezy function not found after timeout'));
            }
          }, interval);
        });
      };

      // Initialize LemonSqueezy and setup event handlers
      const initializeLemonSqueezy = () => {
        // Call createLemonSqueezy to initialize the library
        if (window.createLemonSqueezy && !window.LemonSqueezy) {
          console.log('[LemonSqueezy] Calling createLemonSqueezy()...');
          window.createLemonSqueezy();
        }
        
        if (!window.LemonSqueezy) {
          console.error('[LemonSqueezy] LemonSqueezy object not available after createLemonSqueezy()');
          return false;
        }

        // Setup event handlers
        window.LemonSqueezy.Setup({
          eventHandler: async (data) => {
            console.log('[LemonSqueezy] Event:', data.event, data);
            
            // Handle checkout completed
            if (data.event === 'Checkout.Success') {
              setShowPlanModal(false);
              setShowCreditsModal(false);
              setShowSeatsModal(false);
              setSelectedPlan(null);
              setSelectedCreditPack(null);
              setCheckoutLoading(false);
              
              toast.success('Payment successful! Updating your account...', { duration: 4000 });
              
              // Poll for updated data - the webhook may take a moment to process
              const pollForUpdates = async (attempts = 0, maxAttempts = 5) => {
                // Wait before each attempt (increasing delay)
                const delay = Math.min(1000 * (attempts + 1), 3000); // 1s, 2s, 3s, 3s, 3s
                await new Promise(resolve => setTimeout(resolve, delay));
                
                // Refetch the data
                await Promise.all([
                  queryClient.refetchQueries({ queryKey: ['creditBalance'] }),
                  queryClient.refetchQueries({ queryKey: ['billingUsage'] })
                ]);
                
                // Also refresh company data in auth store (for plan changes)
                await useAuthStore.getState().refreshCompany();
                
                console.log(`[LemonSqueezy] Polling for updated data (attempt ${attempts + 1}/${maxAttempts})`);
                
                // Continue polling if we haven't reached max attempts
                if (attempts < maxAttempts - 1) {
                  pollForUpdates(attempts + 1, maxAttempts);
                }
              };
              
              // Start polling
              pollForUpdates();
            }
            
            // Handle checkout closed
            if (data.event === 'Checkout.Closed') {
              setCheckoutLoading(false);
            }
          }
        });
        
        setLemonSqueezyLoaded(true);
        console.log('[LemonSqueezy] Initialized successfully');
        return true;
      };

      try {
        // Check if createLemonSqueezy is already available (script already loaded)
        if (window.createLemonSqueezy) {
          console.log('[LemonSqueezy] createLemonSqueezy already available');
          initializeLemonSqueezy();
          return;
        }

        // Check if script is already in DOM
        const existingScript = document.querySelector('script[src*="lemonsqueezy"]');
        if (existingScript) {
          console.log('[LemonSqueezy] Script already in DOM, waiting for createLemonSqueezy...');
          try {
            await waitForCreateFunction();
            initializeLemonSqueezy();
          } catch (err) {
            console.error('[LemonSqueezy] Timeout waiting for createLemonSqueezy:', err);
          }
          return;
        }

        // Load Lemon.js script
        const script = document.createElement('script');
        script.src = 'https://app.lemonsqueezy.com/js/lemon.js';
        script.defer = true;
        
        script.onload = async () => {
          console.log('[LemonSqueezy] Script loaded, waiting for createLemonSqueezy...');
          try {
            // Wait for createLemonSqueezy function to be available
            await waitForCreateFunction();
            initializeLemonSqueezy();
          } catch (initError) {
            console.error('[LemonSqueezy] Initialization timeout:', initError);
          }
        };
        
        script.onerror = (error) => {
          console.error('[LemonSqueezy] Script failed to load:', error);
        };
        
        document.head.appendChild(script);
      } catch (error) {
        console.error('[LemonSqueezy] Failed to load:', error);
      }
    };

    loadLemonSqueezy();
  }, [queryClient]);

  // Handle URL action params (e.g., from clicking credits in header)
  useEffect(() => {
    const action = searchParams.get('action');
    const success = searchParams.get('success');
    
    if (action === 'buy-credits') {
      setShowCreditsModal(true);
      // Clear the action param
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    } else if (action === 'buy-seats') {
      setShowSeatsModal(true);
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
    
    // Handle success redirects from Lemon Squeezy
    if (success) {
      if (success === 'subscription') {
        toast.success('Subscription updated successfully!');
      } else if (success === 'credits') {
        toast.success('Credits purchased successfully!');
      } else if (success === 'seats') {
        toast.success('Additional seats purchased successfully!');
      }
      searchParams.delete('success');
      setSearchParams(searchParams, { replace: true });
      // Refresh data
      queryClient.invalidateQueries(['creditBalance']);
      queryClient.invalidateQueries(['billingUsage']);
    }
  }, [searchParams, setSearchParams, queryClient]);
  
  // Pass companyId for Super/Managing Admins
  const { data: usageData, isLoading: usageLoading } = useQuery({
    queryKey: ['billingUsage', companyId],
    queryFn: () => apiHelpers.getUsage(companyId),
    enabled: !noCompanySelected
  });
  
  const { data: storageData } = useQuery({
    queryKey: ['storageUsage', companyId],
    queryFn: () => apiHelpers.getStorageUsage(companyId),
    enabled: !noCompanySelected
  });
  
  // Fetch credit transactions for usage chart
  const { data: creditTransactionsData } = useQuery({
    queryKey: ['creditTransactions', companyId],
    queryFn: async () => {
      const result = await apiHelpers.getCreditTransactions({ limit: 100, companyId });
      return result.data; // Unwrap axios response
    },
    enabled: !noCompanySelected
  });
  
  const { data: plansData } = useQuery({
    queryKey: ['subscriptionPlans'],
    queryFn: () => apiHelpers.getSubscriptionPlans()
  });
  
  const { data: currenciesData } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => apiHelpers.getCurrencies()
  });
  
  const { data: creditPacksData } = useQuery({
    queryKey: ['creditPacks'],
    queryFn: async () => {
      const result = await apiHelpers.getCreditPacks();
      return result.data;
    }
  });
  
  const { data: additionalSeatsPricingData } = useQuery({
    queryKey: ['additionalSeatsPricing'],
    queryFn: async () => {
      const result = await apiHelpers.getAdditionalSeatsPricing();
      return result.data;
    }
  });
  
  // Fetch beta status for the company
  const { data: betaStatus } = useQuery({
    queryKey: ['betaStatus', companyId],
    queryFn: async () => {
      if (!companyId) return null;
      const result = await apiHelpers.getBetaStatus(companyId);
      return result.data;
    },
    enabled: !!companyId
  });
  
  // Beta pricing query is defined later after activeCurrency is computed
  
  const { data: creditBalance } = useQuery({
    queryKey: ['creditBalance', companyId],
    queryFn: async () => {
      const result = await apiHelpers.getCreditBalance(companyId);
      return result.data; // Unwrap axios response to match MainLayout
    },
    enabled: !noCompanySelected
  });
  
  const usage = usageData?.data;
  const storage = storageData?.data;
  const plans = plansData?.data?.plans || [];
  const credits = creditBalance; // Already unwrapped
  const creditTransactions = creditTransactionsData?.transactions || []; // Already unwrapped
  const currencies = currenciesData?.data?.currencies || [];
  
  // Debug logging - remove after fixing
  if (plans.length > 0) {
    console.log('[BillingUsage] Plans loaded:', {
      planCount: plans.length,
      plansWithPricing: plans.filter(p => p.pricing?.length > 0).length,
      samplePlan: plans[0] ? {
        slug: plans[0].slug,
        name: plans[0].name,
        pricingCount: plans[0].pricing?.length,
        pricing: plans[0].pricing
      } : null
    });
  }
  const defaultCurrency = currenciesData?.data?.defaultCurrency || currencies.find(c => c.isDefault) || currencies[0];
  const creditPacks = creditPacksData?.packs || [];
  
  // Get company's preferred currency (if set) or default to system default
  const preferredCurrency = company?.settings?.preferredCurrency || defaultCurrency?.code || 'USD';
  
  // Initialize display currency when data loads
  useEffect(() => {
    if (!displayCurrency && preferredCurrency) {
      setDisplayCurrency(preferredCurrency);
    }
  }, [preferredCurrency, displayCurrency]);
  
  // The active currency for display and checkout (falls back to defaults)
  const activeCurrency = displayCurrency || preferredCurrency || defaultCurrency?.code || 'USD';
  const activeCurrencyData = currencies.find(c => c.code === activeCurrency) || defaultCurrency;
  
  // Debug logging - remove after fixing
  console.log('[BillingUsage] Currency state:', {
    displayCurrency,
    preferredCurrency,
    defaultCurrencyCode: defaultCurrency?.code,
    activeCurrency,
    currenciesCount: currencies.length
  });
  
  // Fetch beta pricing for Basic, Pro, and Business plans (refetches when currency changes)
  const { data: betaPricingData } = useQuery({
    queryKey: ['betaPricing', companyId, activeCurrency],
    queryFn: async () => {
      const [basicResult, proResult, businessResult] = await Promise.all([
        apiHelpers.getBetaPricing('basic', companyId, activeCurrency).catch(() => ({ data: null })),
        apiHelpers.getBetaPricing('pro', companyId, activeCurrency).catch(() => ({ data: null })),
        apiHelpers.getBetaPricing('business', companyId, activeCurrency).catch(() => ({ data: null }))
      ]);
      return {
        basic: basicResult.data,
        pro: proResult.data,
        business: businessResult.data
      };
    },
    enabled: !!companyId && !!betaStatus?.isBetaUser && !!activeCurrency
  });
  
  // Get current plan from API response (most reliable) or fall back to company object in auth store
  // The creditBalance API returns the current plan which is always fresh from the server
  const currentPlan = creditBalance?.plan?.slug || company?.subscriptionPlanId?.slug || company?.billingPlan || 'starter';
  
  // Process credit transactions for chart (group by date)
  // Transaction types: 'debit' for prepaid usage, 'postpaid_usage' for postpaid companies
  // Note: debit amounts are stored as negative, so we use Math.abs() to show positive "usage"
  const creditUsageByDate = creditTransactions
    .filter(t => t.type === 'debit' || t.type === 'postpaid_usage')
    .reduce((acc, t) => {
      const date = new Date(t.createdAt).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = { date, total: 0, byFeature: {} };
      }
      // Use absolute value since debits are stored as negative but we want to show positive usage
      const usedAmount = Math.abs(t.amount);
      acc[date].total += usedAmount;
      if (t.featureKey) {
        acc[date].byFeature[t.featureKey] = (acc[date].byFeature[t.featureKey] || 0) + usedAmount;
      }
      return acc;
    }, {});
  
  const creditUsageData = Object.values(creditUsageByDate)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(-14); // Last 14 days
  
  // Enterprise inquiry mutation
  const enterpriseMutation = useMutation({
    mutationFn: (data) => apiHelpers.submitEnterpriseInquiry(data),
    onSuccess: () => {
      toast.success('Your enterprise inquiry has been submitted! We\'ll contact you soon.');
      setShowEnterpriseModal(false);
      setEnterpriseForm({
        companyName: company?.name || '',
        contactName: '',
        email: '',
        phone: '',
        expectedSeats: '',
        expectedStorage: '',
        message: ''
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to submit inquiry');
    }
  });
  
  // Credit usage chart options
  const creditChartOptions = {
    tooltip: { 
      trigger: 'axis',
      formatter: (params) => {
        const data = params[0];
        return `${data.name}<br/>Credits Used: <strong>${data.value}</strong>`;
      }
    },
    xAxis: {
      type: 'category',
      data: creditUsageData.map(d => d.date),
      axisLabel: { 
        rotate: 45,
        fontSize: 10
      }
    },
    yAxis: { 
      type: 'value',
      name: 'Credits'
    },
    series: [
      {
        name: 'Credits Used',
        type: 'bar',
        data: creditUsageData.map(d => d.total),
        itemStyle: { 
          color: '#8b5cf6',
          borderRadius: [4, 4, 0, 0]
        }
      }
    ],
    grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true }
  };
  
  const handlePlanSelect = (plan) => {
    if (plan.slug === 'enterprise') {
      setShowEnterpriseModal(true);
      setShowPlanModal(false);
    } else {
      setSelectedPlan(plan);
    }
  };

  // Handle Lemon Squeezy subscription checkout (or update existing subscription)
  const handleSubscriptionCheckout = useCallback(async (planSlug, currencyCode) => {
    setCheckoutLoading(true);
    try {
      const response = await apiHelpers.createSubscriptionCheckout({
        planSlug,
        currencyCode,
        successUrl: `${window.location.origin}/billing?success=subscription`,
        cancelUrl: `${window.location.origin}/billing`
      });

      // Check if this was an update to existing subscription (no checkout needed)
      if (response.data.updated) {
        // Show appropriate toast based on upgrade vs downgrade
        if (response.data.isUpgrade) {
          toast.success(response.data.message || 'Plan upgraded successfully!', {
            duration: 5000,
            icon: '🎉'
          });
        } else {
          toast.success(response.data.message || 'Plan change scheduled.', {
            duration: 5000,
            icon: '📅'
          });
        }
        setCheckoutLoading(false);
        setShowPlanModal(false);
        setSelectedPlan(null);
        
        // Refresh all billing data and company info
        // Use refetchQueries to ensure data is actually fetched, not just marked stale
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['creditBalance'] }),
          queryClient.refetchQueries({ queryKey: ['billingUsage'] })
        ]);
        
        // Also refresh the company in auth store to update the header and other components
        await useAuthStore.getState().refreshCompany();
        
        return;
      }

      // New subscription - open Lemon Squeezy checkout
      if (!lemonSqueezyLoaded || !window.LemonSqueezy) {
        toast.error('Payment system is loading. Please try again in a moment.');
        setCheckoutLoading(false);
        return;
      }

      window.LemonSqueezy.Url.Open(response.data.checkoutUrl);
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.error || 'Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  }, [lemonSqueezyLoaded, queryClient]);

  // Handle Lemon Squeezy credit pack checkout
  const handleCreditPackCheckout = useCallback(async (packSlug, currencyCode) => {
    if (!lemonSqueezyLoaded || !window.LemonSqueezy) {
      toast.error('Payment system is loading. Please try again in a moment.');
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await apiHelpers.createCreditPackCheckout({
        packSlug,
        currencyCode,
        successUrl: `${window.location.origin}/billing?success=credits`,
        cancelUrl: `${window.location.origin}/billing`
      });

      // Open Lemon Squeezy checkout
      window.LemonSqueezy.Url.Open(response.data.checkoutUrl);
    } catch (error) {
      console.error('Credit pack checkout error:', error);
      toast.error(error.response?.data?.error || 'Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  }, [lemonSqueezyLoaded]);

  // Handle Lemon Squeezy additional seats checkout
  const handleSeatsCheckout = useCallback(async (quantity, currencyCode) => {
    if (!lemonSqueezyLoaded || !window.LemonSqueezy) {
      toast.error('Payment system is loading. Please try again in a moment.');
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await apiHelpers.createSeatsCheckout({
        quantity,
        currencyCode,
        successUrl: `${window.location.origin}/billing?success=seats`,
        cancelUrl: `${window.location.origin}/billing`
      });

      // Open Lemon Squeezy checkout
      window.LemonSqueezy.Url.Open(response.data.checkoutUrl);
    } catch (error) {
      console.error('Seats checkout error:', error);
      toast.error(error.response?.data?.error || 'Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  }, [lemonSqueezyLoaded]);
  
  const handleEnterpriseSubmit = (e) => {
    e.preventDefault();
    enterpriseMutation.mutate({
      ...enterpriseForm,
      inquiryType: 'upgrade'
    });
  };
  
  const getPlanOrder = (slug) => {
    const order = { starter: 0, basic: 1, pro: 2, business: 3, enterprise: 4 };
    return order[slug] || 0;
  };
  
  const isPlanUpgrade = (planSlug) => {
    return getPlanOrder(planSlug) > getPlanOrder(currentPlan);
  };
  
  // Format currency amount
  const formatCurrencyAmount = (amount, currencyCode = 'USD') => {
    if (currencyCode === 'IDR') {
      return `Rp ${amount.toLocaleString('id-ID')}`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  };

  // Get price for default currency (case-insensitive comparison)
  const getPriceForCurrency = (plan, currencyCode) => {
    const upperCode = currencyCode?.toUpperCase();
    return plan.pricing?.find(p => p.currencyCode?.toUpperCase() === upperCode);
  };

  const formatPrice = (plan) => {
    if (plan.isContactSales) return 'Contact Sales';
    const price = getPriceForCurrency(plan, activeCurrency);
    // Debug logging - remove after fixing
    if (!price) {
      console.log('[BillingUsage] formatPrice: No price found', {
        planSlug: plan.slug,
        planName: plan.name,
        activeCurrency,
        pricingArray: plan.pricing,
        availableCurrencies: plan.pricing?.map(p => p.currencyCode)
      });
    }
    if (!price) return 'Not set';
    if (price.amount === 0) return 'Free';
    return `${formatCurrencyAmount(price.amount, activeCurrency)}/mo`;
  };
  
  // Get beta pricing info for a plan
  const getBetaPricingForPlan = (planSlug) => {
    if (!betaStatus?.isBetaUser || !betaPricingData) return null;
    return betaPricingData[planSlug] || null;
  };
  
  // Format beta price display
  const formatBetaPrice = (plan) => {
    const betaPricing = getBetaPricingForPlan(plan.slug);
    if (!betaPricing || !betaPricing.eligible) return null;
    
    if (betaPricing.priceType === 'beta_first_month') {
      return {
        firstMonth: `${formatCurrencyAmount(betaPricing.firstMonthPrice || 0, activeCurrency)}`,
        afterFirstMonth: `${formatCurrencyAmount(betaPricing.foundersPrice || 0, activeCurrency)}/mo`,
        discount: betaPricing.discountPercent || 50,
        regularPrice: formatPrice(plan)
      };
    } else if (betaPricing.priceType === 'founders_discount') {
      return {
        currentPrice: `${formatCurrencyAmount(betaPricing.foundersPrice || 0, activeCurrency)}/mo`,
        discount: betaPricing.discountPercent || 50,
        regularPrice: formatPrice(plan),
        expiresAt: betaPricing.discountExpiresAt
      };
    }
    
    return null;
  };
  
  // Format current plan price - shows discounted price for founders with active discount
  const formatCurrentPlanPrice = (plan) => {
    if (plan.isContactSales) return 'Contact Sales';
    const price = getPriceForCurrency(plan, activeCurrency);
    if (!price) return 'Not set';
    if (price.amount === 0) return 'Free';
    
    // Check if beta user with active discount (50% off for 12 months)
    const isFounderWithDiscount = betaStatus?.isBetaUser && 
                                   betaStatus?.betaFirstMonthUsed && 
                                   betaStatus?.discountExpiresAt &&
                                   new Date(betaStatus.discountExpiresAt) > new Date();
    
    if (isFounderWithDiscount) {
      // Show the discounted price (50% off)
      const discountedPrice = price.amount * 0.5;
      return (
        <span className="flex items-center gap-2">
          <span className="text-green-600 dark:text-green-400 font-bold">
            {formatCurrencyAmount(discountedPrice, activeCurrency)}/mo
          </span>
          <span className="text-xs text-surface-400 line-through">
            {formatCurrencyAmount(price.amount, activeCurrency)}
          </span>
          <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded font-medium">
            50% OFF
          </span>
        </span>
      );
    }
    
    return `${formatCurrencyAmount(price.amount, activeCurrency)}/mo`;
  };

  // Get credit pack price for a currency
  const getPackPriceForCurrency = (pack, currencyCode) => {
    return pack.pricing?.find(p => p.currencyCode === currencyCode);
  };

  // Get the best available price for a credit pack (selected currency first, then any available)
  const getPackBestPrice = (pack) => {
    // First try selected/active currency
    const selectedPrice = getPackPriceForCurrency(pack, activeCurrency);
    if (selectedPrice) {
      return { ...selectedPrice, isDefaultCurrency: true };
    }
    // Fall back to first available pricing
    if (pack.pricing?.length > 0) {
      return { ...pack.pricing[0], isDefaultCurrency: false };
    }
    return null;
  };

  // Format credit pack price
  const formatPackPrice = (pack) => {
    const price = getPackBestPrice(pack);
    if (!price) return 'Not set';
    return formatCurrencyAmount(price.amount, price.currencyCode);
  };

  // Get additional seat price (centralized pricing)
  const getAdditionalSeatPrice = () => {
    const pricing = additionalSeatsPricingData?.pricing?.find(
      p => p.currencyCode === activeCurrency
    );
    return pricing?.pricePerSeat || 0;
  };
  
  // Check if additional seats are configured in Lemon Squeezy
  const additionalSeatsConfigured = additionalSeatsPricingData?.isConfigured || false;
  
  // Show message for Super/Managing Admins without a company selected
  if (noCompanySelected) {
    return (
      <div className="animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            Billing & Usage
          </h1>
          <p className="text-surface-500 dark:text-surface-400 mt-1">
            Monitor your resource consumption and manage your subscription
          </p>
        </div>
        
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
            <BuildingOffice2Icon className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-xl font-display font-bold text-surface-900 dark:text-white mb-2">
            No Company Selected
          </h2>
          <p className="text-surface-500 dark:text-surface-400 max-w-md mx-auto">
            Please select a company from the top navigation bar to view billing and usage information.
          </p>
        </div>
      </div>
    );
  }
  
  if (usageLoading) {
    return (
      <div className="animate-fade-in">
        <div className="skeleton h-8 w-48 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map(i => (
            <div key={i} className="card p-6">
              <div className="skeleton h-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Get full plan data - prefer from plans list, fallback to API response, then default
  const currentPlanData = plans.find(p => p.slug === currentPlan) || 
    (creditBalance?.plan ? { ...creditBalance.plan, slug: creditBalance.plan.slug, name: creditBalance.plan.name } : { name: currentPlan, slug: currentPlan });
  
  // Check if the current plan allows additional seats
  const canBuyAdditionalSeats = currentPlanData.allowAdditionalSeats !== false;
  
  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
          Billing & Usage
        </h1>
        <p className="text-surface-500 dark:text-surface-400 mt-1">
          Monitor your resource consumption and manage your subscription
        </p>
      </div>
      
      {/* Beta Trial Status - shows for beta users only */}
      <div className="mb-6">
        <BetaTrialStatus companyId={companyId} onUpgradeClick={() => setShowPlanModal(true)} />
      </div>
      
      {/* Current Plan Card */}
      <div className="card p-6 mb-8 bg-gradient-to-r from-primary-500/10 to-accent-500/10 border-primary-200 dark:border-primary-800">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-primary-500 flex items-center justify-center">
              <CreditCardIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="text-sm text-surface-500 dark:text-surface-400">Current Plan</p>
              <h2 className="text-2xl font-display font-bold text-surface-900 dark:text-white capitalize">
                {currentPlanData.name || currentPlan}
              </h2>
              <div className="text-sm text-surface-500">
                {currentPlan === 'starter' ? 'Free plan' : formatCurrentPlanPrice(currentPlanData)}
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Credits Display */}
            {credits?.hasCompany && (
              <div className="flex items-center gap-3 px-4 py-2 bg-white dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700">
                <SparklesIcon className="w-5 h-5 text-primary-500" />
                <div>
                  <p className="text-xs text-surface-500">Available Credits</p>
                  <p className="font-semibold text-surface-900 dark:text-white">
                    {credits?.balance?.totalAvailable?.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            )}
            
            <button
              onClick={() => setShowPlanModal(true)}
              className="btn-primary"
            >
              <ArrowUpIcon className="w-5 h-5 mr-2" />
              Change Plan
            </button>
          </div>
        </div>
        
        {/* Credit breakdown */}
        {credits?.hasCompany && (
          <div className="mt-4 pt-4 border-t border-primary-200 dark:border-primary-800 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <ClockIcon className="w-4 h-4 text-surface-500" />
              <div>
                <p className="text-xs text-surface-500">Monthly Credits</p>
                <p className="font-medium text-surface-900 dark:text-white">
                  {credits?.balance?.monthly?.available?.toLocaleString() || 0}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <SparklesIcon className="w-4 h-4 text-surface-500" />
              <div>
                <p className="text-xs text-surface-500">Permanent Credits</p>
                <p className="font-medium text-surface-900 dark:text-white">
                  {credits?.balance?.permanent?.toLocaleString() || 0}
                </p>
              </div>
            </div>
            {credits?.balance?.monthly?.resetDate && (
              <div className="flex items-center gap-2 col-span-2">
                <ClockIcon className="w-4 h-4 text-surface-500" />
                <div>
                  <p className="text-xs text-surface-500">Monthly Credits Reset</p>
                  <p className="font-medium text-surface-900 dark:text-white">
                    {new Date(credits.balance.monthly.resetDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Usage Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Seats */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <UserGroupIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-surface-500">Active Seats</p>
                <p className="text-2xl font-display font-bold text-surface-900 dark:text-white">
                  {usage?.usage?.seats?.current || 0}
                  <span className="text-sm font-normal text-surface-400"> / {usage?.usage?.seats?.limit || 5}</span>
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                if (canBuyAdditionalSeats) {
                  setShowSeatsModal(true);
                } else {
                  toast.error('Your current plan does not allow purchasing additional seats. Please upgrade to another plan.', {
                    duration: 4000
                  });
                }
              }}
              className={`btn-ghost text-sm ${
                canBuyAdditionalSeats 
                  ? 'text-primary-600 hover:text-primary-700' 
                  : 'text-surface-400 cursor-not-allowed opacity-50'
              }`}
              title={canBuyAdditionalSeats ? 'Buy additional seats' : 'Not available on your current plan'}
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Buy Seats
            </button>
          </div>
          <div className="w-full bg-surface-200 dark:bg-surface-700 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all"
              style={{ width: `${Math.min(usage?.usage?.seats?.percentage || 0, 100)}%` }}
            />
          </div>
          <p className="text-xs text-surface-500 mt-2">
            {usage?.usage?.seats?.percentage?.toFixed(1)}% used
          </p>
          {!canBuyAdditionalSeats && (
            <p className="text-xs text-amber-500 mt-1">
              Upgrade your plan to add more seats
            </p>
          )}
        </div>
        
        {/* Storage */}
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <CircleStackIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-surface-500">Storage Used</p>
              <p className="text-2xl font-display font-bold text-surface-900 dark:text-white">
                {usage?.usage?.storage?.currentFormatted || '0 B'}
                <span className="text-sm font-normal text-surface-400"> / {usage?.usage?.storage?.limitFormatted || '10 GB'}</span>
              </p>
            </div>
          </div>
          <div className="w-full bg-surface-200 dark:bg-surface-700 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full transition-all"
              style={{ width: `${Math.min(usage?.usage?.storage?.percentage || 0, 100)}%` }}
            />
          </div>
          <p className="text-xs text-surface-500 mt-2">
            {usage?.usage?.storage?.percentage?.toFixed(1)}% used
          </p>
        </div>
        
        {/* Credits */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-surface-500">Available Credits</p>
                <p className="text-2xl font-display font-bold text-surface-900 dark:text-white">
                  {credits?.balance?.totalAvailable?.toLocaleString() || 0}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowCreditsModal(true)}
              className="btn-ghost text-sm text-purple-600 hover:text-purple-700"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Buy Credits
            </button>
          </div>
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <ClockIcon className="w-4 h-4 text-surface-400" />
              <span className="text-surface-500">Monthly:</span>
              <span className="font-medium text-surface-700 dark:text-surface-300">
                {credits?.balance?.monthly?.available?.toLocaleString() || 0}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <SparklesIcon className="w-4 h-4 text-surface-400" />
              <span className="text-surface-500">Permanent:</span>
              <span className="font-medium text-surface-700 dark:text-surface-300">
                {credits?.balance?.permanent?.toLocaleString() || 0}
              </span>
            </div>
          </div>
          {credits?.balance?.monthly?.resetDate && (
            <p className="text-xs text-surface-500 mt-3">
              Monthly credits reset on {new Date(credits.balance.monthly.resetDate).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Credit Usage Chart */}
        <div className="card">
          <div className="p-4 border-b border-surface-200 dark:border-surface-800">
            <h2 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
              <SparklesIcon className="w-5 h-5 text-purple-500" />
              Credit Usage (Last 14 Days)
            </h2>
          </div>
          <div className="p-4">
            {creditUsageData.length > 0 ? (
              <ReactECharts option={creditChartOptions} style={{ height: 300 }} />
            ) : (
              <div className="h-[300px] flex items-center justify-center text-surface-500">
                No credit usage yet
              </div>
            )}
          </div>
        </div>
        
        {/* Storage Breakdown */}
        <div className="card">
          <div className="p-4 border-b border-surface-200 dark:border-surface-800">
            <h2 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
              <CircleStackIcon className="w-5 h-5 text-green-500" />
              Storage Breakdown
            </h2>
          </div>
          <div className="p-4">
            {storage?.breakdown?.length > 0 ? (
              <div className="space-y-4">
                {storage.breakdown.map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-surface-100 dark:bg-surface-800 flex items-center justify-center">
                        <ChartBarIcon className="w-4 h-4 text-surface-500" />
                      </div>
                      <div>
                        <p className="font-medium text-surface-900 dark:text-white">{item.name}</p>
                        <p className="text-xs text-surface-500">{item.rows?.toLocaleString()} rows</p>
                      </div>
                    </div>
                    <span className="text-surface-600 dark:text-surface-400">
                      {item.sizeFormatted}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-surface-500">
                No data sources yet
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Current Period */}
      <div className="card p-6 mt-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-surface-900 dark:text-white">Current Billing Period</h3>
            <p className="text-sm text-surface-500 mt-1">
              {usage?.currentPeriod?.start && (
                <>
                  {new Date(usage.currentPeriod.start).toLocaleDateString()} - {new Date(usage.currentPeriod.end).toLocaleDateString()}
                </>
              )}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-surface-500">Plan</p>
            <p className="font-medium text-surface-900 dark:text-white capitalize">
              {currentPlanData.name || currentPlan}
            </p>
          </div>
        </div>
      </div>
      
      {/* Transaction History Section */}
      <TransactionHistory companyId={companyId} formatCurrencyAmount={formatCurrencyAmount} />
      
      {/* Plan Selection Modal */}
      <Transition appear show={showPlanModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowPlanModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white dark:bg-surface-900 shadow-2xl transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-surface-200 dark:border-surface-800">
                    <div>
                      <Dialog.Title className="text-xl font-display font-semibold text-surface-900 dark:text-white">
                        Change Your Plan
                      </Dialog.Title>
                      <p className="text-sm text-surface-500 mt-1">
                        Select a plan that best fits your needs
                      </p>
                    </div>
                    <button 
                      onClick={() => setShowPlanModal(false)}
                      className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
                    >
                      <XMarkIcon className="w-5 h-5 text-surface-500" />
                    </button>
                  </div>
                  
                  {/* Plans Grid */}
                  <div className="p-6">
                    {/* Founders Pricing Banner for Beta Users */}
                    {betaStatus?.isBetaUser && !betaStatus?.betaFirstMonthUsed && (
                      <div className="mb-4 p-4 bg-surface-100 dark:bg-surface-700/50 border border-surface-200 dark:border-surface-600 rounded-xl">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">🏆</span>
                          <div>
                            <h4 className="font-semibold text-surface-900 dark:text-white">Founders Pricing Available!</h4>
                            <p className="text-sm text-surface-600 dark:text-surface-300 mt-1">
                              As a beta founder, you get special pricing on all plans:
                            </p>
                            <ul className="mt-2 text-sm text-surface-600 dark:text-surface-300 space-y-1">
                              <li>• <strong className="text-primary-600 dark:text-primary-400">Basic:</strong> Free during beta, then 50% off for your first year</li>
                              <li>• <strong className="text-primary-600 dark:text-primary-400">Pro/Business first month:</strong> $10 Pro / $25 Business</li>
                              <li>• <strong className="text-primary-600 dark:text-primary-400">Pro/Business months 2-12:</strong> 50% off the plan you choose</li>
                              <li>• <strong className="text-surface-500 dark:text-surface-400">After year 1:</strong> Regular price</li>
                            </ul>
                            {betaStatus?.daysRemaining > 0 && betaStatus?.status === 'trial_active' && (
                              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400 font-medium">
                                ⏰ Beta ends in {betaStatus.daysRemaining} days ({betaStatus.trialEndDate ? new Date(betaStatus.trialEndDate).toLocaleDateString() : 'TBD'})
                              </p>
                            )}
                            {betaStatus?.graceDaysRemaining > 0 && betaStatus?.status === 'grace_period' && (
                              <p className="mt-2 text-xs text-red-600 dark:text-red-400 font-medium">
                                ⚠️ Grace period: {betaStatus.graceDaysRemaining} days left to decide!
                              </p>
                            )}
                            <p className="mt-2 text-xs text-surface-500 dark:text-surface-400 italic">
                              Note: If you upgrade to Pro or Business, the 50% discount is locked to that plan. 
                              Switching plans later will be at regular price.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Already Upgraded Banner */}
                    {betaStatus?.isBetaUser && betaStatus?.betaFirstMonthUsed && betaStatus?.upgradedTo && (
                      <div className="mb-4 p-3 bg-surface-100 dark:bg-surface-700/50 border border-surface-200 dark:border-surface-600 rounded-lg">
                        <p className="text-sm text-surface-700 dark:text-surface-300">
                          🏆 Your founders discount is locked to <strong className="text-primary-600 dark:text-primary-400">{betaStatus.upgradedTo.toUpperCase()}</strong>. 
                          Changing to a different plan will be at regular price.
                        </p>
                      </div>
                    )}
                    
                    {/* Currency selector */}
                    {currencies.length > 1 && (
                      <div className="mb-4 flex items-center gap-2 text-sm text-surface-500 dark:text-surface-400">
                        <CreditCardIcon className="w-4 h-4" />
                        <span>Prices shown in</span>
                        <select
                          value={activeCurrency}
                          onChange={(e) => setDisplayCurrency(e.target.value)}
                          className="bg-surface-100 dark:bg-surface-700 border border-surface-300 dark:border-surface-600 rounded px-2 py-1 text-sm text-surface-900 dark:text-white"
                        >
                          {currencies.map(currency => (
                            <option key={currency.code} value={currency.code}>
                              {currency.code} ({currency.name})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                      {plans.map((plan) => {
                        const isCurrent = plan.slug === currentPlan;
                        const isUpgrade = isPlanUpgrade(plan.slug);
                        const features = PLAN_FEATURES[plan.slug]?.features || [];
                        const betaPricing = formatBetaPrice(plan);
                        
                        return (
                          <div
                            key={plan.slug}
                            className={`relative rounded-xl border-2 p-5 transition-all ${
                              isCurrent
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : selectedPlan?.slug === plan.slug
                                  ? 'border-primary-500 bg-surface-50 dark:bg-surface-800'
                                  : betaPricing
                                    ? 'border-purple-300 dark:border-purple-700 hover:border-purple-400 bg-purple-50/50 dark:bg-purple-900/10'
                                    : 'border-surface-200 dark:border-surface-700 hover:border-primary-300'
                            }`}
                          >
                            {isCurrent && (
                              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-full">
                                Current Plan
                              </div>
                            )}
                            
                            {/* Founders Badge for beta pricing */}
                            {betaPricing && !isCurrent && (
                              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-purple-500 to-amber-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                                <span>🏆</span> Founders Price
                              </div>
                            )}
                            
                            <div className="text-center mb-4">
                              <h3 className="font-display font-bold text-lg text-surface-900 dark:text-white">
                                {plan.name}
                              </h3>
                              
                              {/* Beta Pricing Display */}
                              {betaPricing ? (
                                <div className="mt-2">
                                  {betaPricing.firstMonth ? (
                                    <>
                                      {/* First month special price */}
                                      <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                        {betaPricing.firstMonth}
                                      </p>
                                      <p className="text-xs text-purple-500 font-medium">first month</p>
                                      <p className="text-sm text-surface-500 mt-1">
                                        then {betaPricing.afterFirstMonth}
                                      </p>
                                      <p className="text-xs text-surface-400 line-through">
                                        Regular: {betaPricing.regularPrice}
                                      </p>
                                      <p className="text-xs text-emerald-500 font-medium mt-1">
                                        {betaPricing.discount}% off for 1 year!
                                      </p>
                                    </>
                                  ) : (
                                    <>
                                      {/* Already using founders discount */}
                                      <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                        {betaPricing.currentPrice}
                                      </p>
                                      <p className="text-xs text-surface-400 line-through">
                                        Regular: {betaPricing.regularPrice}
                                      </p>
                                      <p className="text-xs text-emerald-500 font-medium">
                                        {betaPricing.discount}% founders discount
                                      </p>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <p className={`text-2xl font-bold mt-2 ${
                                  !getPriceForCurrency(plan, activeCurrency) && !plan.isContactSales 
                                    ? 'text-amber-500' 
                                    : 'text-primary-600 dark:text-primary-400'
                                }`}>
                                  {formatPrice(plan)}
                                </p>
                              )}
                            </div>
                            
                            <ul className="space-y-2 mb-4">
                              {features.map((feature, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-surface-600 dark:text-surface-400">
                                  <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                            
                            {!isCurrent && (
                              <button
                                onClick={() => handlePlanSelect(plan)}
                                className={`w-full py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
                                  plan.isContactSales
                                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                                    : betaPricing
                                      ? 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white'
                                      : isUpgrade
                                        ? 'bg-primary-500 hover:bg-primary-600 text-white'
                                        : 'bg-surface-200 dark:bg-surface-700 hover:bg-surface-300 dark:hover:bg-surface-600 text-surface-700 dark:text-surface-300'
                                }`}
                              >
                                {plan.isContactSales ? (
                                  <>
                                    <EnvelopeIcon className="w-4 h-4 inline mr-1" />
                                    Contact Sales
                                  </>
                                ) : betaPricing ? (
                                  <>
                                    <SparklesIcon className="w-4 h-4 inline mr-1" />
                                    Upgrade with Founders Price
                                  </>
                                ) : isUpgrade ? (
                                  <>
                                    <ArrowUpIcon className="w-4 h-4 inline mr-1" />
                                    Upgrade
                                  </>
                                ) : (
                                  <>
                                    <ArrowDownIcon className="w-4 h-4 inline mr-1" />
                                    Downgrade
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Selected Plan Confirmation */}
                    {selectedPlan && !selectedPlan.isContactSales && (
                      <div className={`mt-6 p-4 rounded-xl ${
                        formatBetaPrice(selectedPlan) 
                          ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800'
                          : 'bg-surface-100 dark:bg-surface-800'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-surface-500">
                              {isPlanUpgrade(selectedPlan.slug) ? 'Upgrading' : 'Downgrading'} to:
                            </p>
                            <p className="font-semibold text-surface-900 dark:text-white">
                              {selectedPlan.name}
                            </p>
                            
                            {/* Show beta pricing details if applicable */}
                            {formatBetaPrice(selectedPlan) ? (
                              <div className="mt-1">
                                {formatBetaPrice(selectedPlan).firstMonth ? (
                                  <>
                                    <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                      {formatBetaPrice(selectedPlan).firstMonth} first month
                                    </p>
                                    <p className="text-sm text-surface-600 dark:text-surface-400">
                                      then {formatBetaPrice(selectedPlan).afterFirstMonth} ({formatBetaPrice(selectedPlan).discount}% off for 1 year)
                                    </p>
                                    <p className="text-xs text-surface-400 line-through">
                                      Regular price: {formatBetaPrice(selectedPlan).regularPrice}
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                      {formatBetaPrice(selectedPlan).currentPrice}
                                    </p>
                                    <p className="text-xs text-surface-400 line-through">
                                      Regular price: {formatBetaPrice(selectedPlan).regularPrice}
                                    </p>
                                  </>
                                )}
                                <div className="flex items-center gap-1 mt-2 text-sm text-purple-600 dark:text-purple-400">
                                  <span>🏆</span>
                                  <span className="font-medium">Founders Discount Applied!</span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-surface-700 dark:text-surface-300">
                                {formatPrice(selectedPlan)}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-3">
                            <button
                              onClick={() => setSelectedPlan(null)}
                              className="btn-secondary"
                              disabled={checkoutLoading}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleSubscriptionCheckout(selectedPlan.slug, activeCurrency)}
                              disabled={checkoutLoading || !lemonSqueezyLoaded}
                              className={formatBetaPrice(selectedPlan) 
                                ? 'btn-primary bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
                                : 'btn-primary'
                              }
                            >
                              {checkoutLoading ? (
                                <>
                                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                                  Processing...
                                </>
                              ) : (
                                'Proceed to Payment'
                              )}
                            </button>
                          </div>
                        </div>
                        
                        {!isPlanUpgrade(selectedPlan.slug) && (
                          <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                            <p className="text-sm text-amber-700 dark:text-amber-300">
                              ⚠️ Downgrading may result in loss of features and data. Changes will take effect at the end of your current billing period.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      
      {/* Enterprise Contact Modal */}
      <Transition appear show={showEnterpriseModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowEnterpriseModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-surface-900 shadow-2xl transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-surface-200 dark:border-surface-800">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                        <BuildingOffice2Icon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <Dialog.Title className="text-lg font-display font-semibold text-surface-900 dark:text-white">
                        Enterprise Plan Inquiry
                      </Dialog.Title>
                    </div>
                    <button 
                      onClick={() => setShowEnterpriseModal(false)}
                      className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
                    >
                      <XMarkIcon className="w-5 h-5 text-surface-500" />
                    </button>
                  </div>
                  
                  {/* Form */}
                  <form onSubmit={handleEnterpriseSubmit} className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Company Name</label>
                        <input
                          type="text"
                          value={enterpriseForm.companyName}
                          onChange={(e) => setEnterpriseForm(p => ({ ...p, companyName: e.target.value }))}
                          className="input"
                          required
                        />
                      </div>
                      <div>
                        <label className="label">Contact Name</label>
                        <input
                          type="text"
                          value={enterpriseForm.contactName}
                          onChange={(e) => setEnterpriseForm(p => ({ ...p, contactName: e.target.value }))}
                          className="input"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Email</label>
                        <input
                          type="email"
                          value={enterpriseForm.email}
                          onChange={(e) => setEnterpriseForm(p => ({ ...p, email: e.target.value }))}
                          className="input"
                          required
                        />
                      </div>
                      <div>
                        <label className="label">Phone (Optional)</label>
                        <input
                          type="tel"
                          value={enterpriseForm.phone}
                          onChange={(e) => setEnterpriseForm(p => ({ ...p, phone: e.target.value }))}
                          className="input"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Expected User Seats</label>
                        <input
                          type="number"
                          value={enterpriseForm.expectedSeats}
                          onChange={(e) => setEnterpriseForm(p => ({ ...p, expectedSeats: e.target.value }))}
                          className="input"
                          placeholder="e.g., 50"
                        />
                      </div>
                      <div>
                        <label className="label">Expected Storage (GB)</label>
                        <input
                          type="number"
                          value={enterpriseForm.expectedStorage}
                          onChange={(e) => setEnterpriseForm(p => ({ ...p, expectedStorage: e.target.value }))}
                          className="input"
                          placeholder="e.g., 1000"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="label">Message (Optional)</label>
                      <textarea
                        value={enterpriseForm.message}
                        onChange={(e) => setEnterpriseForm(p => ({ ...p, message: e.target.value }))}
                        className="input"
                        rows={3}
                        placeholder="Tell us about your requirements..."
                      />
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowEnterpriseModal(false)}
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={enterpriseMutation.isPending}
                        className="btn-primary"
                      >
                        {enterpriseMutation.isPending ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <EnvelopeIcon className="w-5 h-5 mr-2" />
                            Submit Inquiry
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      
      {/* Purchase Credits Modal */}
      <Transition appear show={showCreditsModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowCreditsModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white dark:bg-surface-900 shadow-2xl transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-surface-200 dark:border-surface-800">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <SparklesIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <Dialog.Title className="text-lg font-display font-semibold text-surface-900 dark:text-white">
                          Purchase Credits
                        </Dialog.Title>
                        <p className="text-sm text-surface-500">
                          Select a credit pack to power your AI features
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setShowCreditsModal(false)}
                      className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
                    >
                      <XMarkIcon className="w-5 h-5 text-surface-500" />
                    </button>
                  </div>
                  
                  {/* Currency selector */}
                  {currencies.length > 1 && (
                    <div className="px-6 pt-4 flex items-center gap-2 text-sm text-surface-500">
                      <CreditCardIcon className="w-4 h-4" />
                      <span>Prices shown in</span>
                      <select
                        value={activeCurrency}
                        onChange={(e) => setDisplayCurrency(e.target.value)}
                        className="bg-surface-100 dark:bg-surface-700 border border-surface-300 dark:border-surface-600 rounded px-2 py-1 text-sm text-surface-900 dark:text-white"
                      >
                        {currencies.map(currency => (
                          <option key={currency.code} value={currency.code}>
                            {currency.code} ({currency.name})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Credit Packs Grid */}
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {creditPacks.map((pack) => {
                        const bestPrice = getPackBestPrice(pack);
                        const isSelected = selectedCreditPack?.slug === pack.slug;
                        const totalCredits = pack.credits + (pack.bonusCredits || 0);
                        
                        return (
                          <div
                            key={pack.slug}
                            onClick={() => setSelectedCreditPack(pack)}
                            className={`relative rounded-xl border-2 p-5 cursor-pointer transition-all ${
                              pack.isFeatured ? 'ring-2 ring-purple-500' : ''
                            } ${
                              isSelected
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                                : 'border-surface-200 dark:border-surface-700 hover:border-purple-300'
                            }`}
                          >
                            {pack.isFeatured && (
                              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-500 text-white text-xs font-medium rounded-full">
                                Best Value
                              </div>
                            )}
                            
                            <div className="text-center mb-4">
                              <div className="w-12 h-12 mx-auto rounded-xl bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center mb-3">
                                <CubeIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                              </div>
                              <h3 className="font-display font-bold text-lg text-surface-900 dark:text-white">
                                {pack.name}
                              </h3>
                              <div className="mt-2">
                                <span className="text-3xl font-bold text-surface-900 dark:text-white">
                                  {pack.credits.toLocaleString()}
                                </span>
                                {pack.bonusCredits > 0 && (
                                  <span className="text-sm text-purple-500 ml-1">
                                    +{pack.bonusCredits} bonus
                                  </span>
                                )}
                                <p className="text-sm text-surface-500">credits</p>
                              </div>
                            </div>
                            
                            <div className="text-center mb-3">
                              <p className={`text-2xl font-bold ${
                                !bestPrice ? 'text-amber-500' : 'text-purple-600 dark:text-purple-400'
                              }`}>
                                {bestPrice ? formatCurrencyAmount(bestPrice.amount, bestPrice.currencyCode) : 'Not set'}
                              </p>
                            </div>
                            
                            {bestPrice && (
                              <p className="text-center text-xs text-surface-500">
                                {formatCurrencyAmount(bestPrice.amount / totalCredits, bestPrice.currencyCode)}/credit
                              </p>
                            )}
                            
                            <p className="text-center text-sm text-surface-500 mt-3">
                              {pack.description}
                            </p>
                            
                            {isSelected && (
                              <div className="absolute top-3 right-3">
                                <CheckIcon className="w-6 h-6 text-purple-500" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Selected Pack Confirmation */}
                    {selectedCreditPack && (
                      <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-surface-500">Selected Pack:</p>
                            <p className="font-semibold text-surface-900 dark:text-white">
                              {selectedCreditPack.name} - {(selectedCreditPack.credits + (selectedCreditPack.bonusCredits || 0)).toLocaleString()} credits
                            </p>
                            <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                              {formatPackPrice(selectedCreditPack)}
                            </p>
                          </div>
                          <div className="flex gap-3">
                            <button
                              onClick={() => setSelectedCreditPack(null)}
                              className="btn-secondary"
                              disabled={checkoutLoading}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleCreditPackCheckout(selectedCreditPack.slug, activeCurrency)}
                              disabled={checkoutLoading || !lemonSqueezyLoaded}
                              className="btn-primary bg-purple-600 hover:bg-purple-700"
                            >
                              {checkoutLoading ? (
                                <>
                                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <ShoppingCartIcon className="w-5 h-5 mr-2" />
                                  Proceed to Payment
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      
      {/* Buy Additional Seats Modal */}
      <Transition appear show={showSeatsModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowSeatsModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-surface-900 shadow-2xl transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-surface-200 dark:border-surface-800">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                        <UserGroupIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <Dialog.Title className="text-lg font-display font-semibold text-surface-900 dark:text-white">
                        Buy Additional Seats
                      </Dialog.Title>
                    </div>
                    <button 
                      onClick={() => setShowSeatsModal(false)}
                      className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
                    >
                      <XMarkIcon className="w-5 h-5 text-surface-500" />
                    </button>
                  </div>
                  
                  <div className="p-6">
                    {/* Show upgrade message if plan doesn't allow additional seats */}
                    {!canBuyAdditionalSeats ? (
                      <div className="text-center py-6">
                        <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
                          <UserGroupIcon className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-2">
                          Additional Seats Not Available
                        </h3>
                        <p className="text-surface-500 dark:text-surface-400 mb-6">
                          The plan you are on does not allow you to buy additional seats. Please upgrade to another plan to add more team members.
                        </p>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setShowSeatsModal(false)}
                            className="btn-secondary flex-1"
                          >
                            Close
                          </button>
                          <button
                            onClick={() => {
                              setShowSeatsModal(false);
                              setShowPlanModal(true);
                            }}
                            className="btn-primary flex-1"
                          >
                            <ArrowUpIcon className="w-5 h-5 mr-2" />
                            Upgrade Plan
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                    {/* Currency selector */}
                    {currencies.length > 1 && (
                      <div className="mb-4 flex items-center gap-2 text-sm text-surface-500 dark:text-surface-400">
                        <CreditCardIcon className="w-4 h-4" />
                        <span>Prices shown in</span>
                        <select
                          value={activeCurrency}
                          onChange={(e) => setDisplayCurrency(e.target.value)}
                          className="bg-surface-100 dark:bg-surface-700 border border-surface-300 dark:border-surface-600 rounded px-2 py-1 text-sm text-surface-900 dark:text-white"
                        >
                          {currencies.map(currency => (
                            <option key={currency.code} value={currency.code}>
                              {currency.code} ({currency.name})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Current Usage */}
                    <div className="mb-6 p-4 bg-surface-100 dark:bg-surface-800 rounded-xl">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-surface-500">Current Seats</span>
                        <span className="font-semibold text-surface-900 dark:text-white">
                          {usage?.usage?.seats?.current || 0} / {usage?.usage?.seats?.limit || 5}
                        </span>
                      </div>
                      <div className="w-full bg-surface-200 dark:bg-surface-700 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(usage?.usage?.seats?.percentage || 0, 100)}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Seat Selection */}
                    <div className="mb-6">
                      <label className="label">Number of Additional Seats</label>
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => setAdditionalSeats(Math.max(1, additionalSeats - 1))}
                          className="w-10 h-10 rounded-lg border border-surface-300 dark:border-surface-600 flex items-center justify-center hover:bg-surface-100 dark:hover:bg-surface-800"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={additionalSeats}
                          onChange={(e) => setAdditionalSeats(Math.max(1, parseInt(e.target.value) || 1))}
                          className="input text-center w-20"
                          min="1"
                        />
                        <button
                          onClick={() => setAdditionalSeats(additionalSeats + 1)}
                          className="w-10 h-10 rounded-lg border border-surface-300 dark:border-surface-600 flex items-center justify-center hover:bg-surface-100 dark:hover:bg-surface-800"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    {/* Pricing */}
                    <div className="mb-6 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-primary-200 dark:border-primary-800">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-surface-600 dark:text-surface-400">Price per seat/month</span>
                        <span className="font-medium text-surface-900 dark:text-white">
                          {getAdditionalSeatPrice() > 0 
                            ? formatCurrencyAmount(getAdditionalSeatPrice(), activeCurrency)
                            : 'Contact Sales'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-surface-600 dark:text-surface-400">Additional seats</span>
                        <span className="font-medium text-surface-900 dark:text-white">
                          × {additionalSeats}
                        </span>
                      </div>
                      <div className="border-t border-primary-200 dark:border-primary-700 my-2 pt-2">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-surface-900 dark:text-white">Total/month</span>
                          <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                            {getAdditionalSeatPrice() > 0 
                              ? formatCurrencyAmount(getAdditionalSeatPrice() * additionalSeats, activeCurrency)
                              : 'Contact Sales'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* New Total */}
                    <div className="mb-6 text-center text-sm text-surface-500">
                      After purchase: <span className="font-semibold text-surface-900 dark:text-white">
                        {(usage?.usage?.seats?.limit || 5) + additionalSeats} total seats
                      </span>
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowSeatsModal(false)}
                        className="btn-secondary flex-1"
                        disabled={checkoutLoading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSeatsCheckout(additionalSeats, activeCurrency)}
                        disabled={checkoutLoading || !lemonSqueezyLoaded || !additionalSeatsConfigured}
                        className="btn-primary flex-1"
                        title={!additionalSeatsConfigured ? 'Additional seats not configured yet' : ''}
                      >
                        {checkoutLoading ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                            Processing...
                          </>
                        ) : !additionalSeatsConfigured ? (
                          'Not Available'
                        ) : (
                          <>
                            <ShoppingCartIcon className="w-5 h-5 mr-2" />
                            Proceed to Payment
                          </>
                        )}
                      </button>
                    </div>
                      </>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
